import * as Sentry from "@sentry/vue"

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

// Unset or unparseable means "trace everything": the rate is tuned per
// environment, and a typo in it should not silently switch tracing off.
const parseTracesSampleRate = (raw) => {
  const rate = Number.parseFloat(raw)
  return Number.isFinite(rate) && rate >= 0 && rate <= 1 ? rate : 1.0
}

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// `sentry-trace` / `baggage` headers go ONLY to our own API backends. Any other
// host (Strapi, PostHog, Stripe, Pusher, marketplaces) would see two unknown
// headers, fail its CORS preflight and break the request. Matched by origin
// with an anchored regex, because a plain string target is a substring match
// and would also hit e.g. `https://our.host.example.com` or a query param.
// Matched on the base PATH too, not just the origin: the API and the batch
// processor share one host (`/api/v2` vs `/v2`).
//
// The batch processor is left out: its CORS allow-list is fixed and doesn't
// include either header, so its preflight would fail and every batch-processor
// call with it. Its requests are still traced as spans; they just don't carry
// the trace into its own logs. Add it back once it allows both headers.
const buildTracePropagationTargets = () => {
  const apiHosts = [
    import.meta.env.VITE_API_HOST,
  ]
  const bases = new Set()
  apiHosts.forEach((host) => {
    if (!host) return
    try {
      const url = new URL(host)
      bases.add(`${url.origin}${url.pathname.replace(/\/+$/, '')}`)
    } catch {
      // A malformed env value just means no propagation to that host.
    }
  })
  return [...bases].map((base) => new RegExp(`^${escapeRegExp(base)}(?:[/?#]|$)`))
}

export const SentryInit = (app, router) => {

  Sentry.init({
    beforeSend(event, hint) {
      // Filter out unhandled rejections with no meaningful value (e.g. Promise.reject(undefined))
      const isUnhandledRejection = hint?.originalException === undefined || hint?.originalException === null
      const isPromiseRejectionEvent = event.exception?.values?.[0]?.type === 'UnhandledRejection' ||
        event.exception?.values?.[0]?.value?.includes('CustomEvent')
      if (isUnhandledRejection && isPromiseRejectionEvent) {
        return null
      }

      // Filter out image load errors
      if (event.exception?.values?.[0]?.value?.includes("Image failed to load!And failed src was")) {
        return false
      }

      // Add additional context
      if (event.user) {
        // User context already set
      }

      // Add custom tags for better filtering in Sentry
      event.tags = {
        ...(event.tags || {}),
        environment: import.meta.env.MODE || 'development',
        component: hint?.component?.__name || 'unknown',
      }

      return event
    },
    app,
    dsn: SENTRY_DSN,

    // Performance tracing is OFF for now: spans count against the Sentry quota
    // and can be billed past it. Uncomment the three options below to turn it
    // on, and set VITE_SENTRY_TRACES_SAMPLE_RATE low on prod (e.g. 0.1).
    //
    // An array is ADDED to the default integrations, not swapped for them, so
    // error capture keeps its defaults. Tracing gives pageload/navigation
    // transactions named after the route, with an http.client span (URL,
    // method, status, duration) for every XHR/fetch — axios included.
    // integrations: [Sentry.browserTracingIntegration({ router })],
    //
    // tracesSampleRate: parseTracesSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    //
    // tracePropagationTargets: buildTracePropagationTargets(),
  
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [/^Image failed to load!And failed src was$/, 'Request aborted', 'Failed to fetch'],
    denyUrls: [],
    
    // Automatically attach user info
    attachStacktrace: true,
    
    // Capture unhandled promise rejections
    captureUnhandledRejections: true,
  })
}

// Helper function to set user context (call this when user logs in)
export const setSentryUser = (user) => {
  // Logout passes null: clear the user outright, rather than leaving an empty
  // `{ id: undefined, ... }` object on the scope.
  if (!user) {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({
    id: user?.id,
    email: user?.email,
    username: user?.username || user?.name,
  })
}

// Helper function to add breadcrumbs
export const addBreadcrumb = (message, category = 'custom', level = 'info', data = {}) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  })
}

// Helper function to manually capture exceptions with context
export const captureException = (error, context = {}) => {
  Sentry.withScope((scope) => {
    // Add custom context
    Object.keys(context).forEach(key => {
      scope.setContext(key, context[key])
    })
    Sentry.captureException(error)
  })
}

// Helper function to send a standalone event (no error) with context + tags.
// Use for diagnostics that must surface in Sentry even when nothing throws —
// e.g. on-device flows where the console isn't reachable. Filter in Sentry by
// the tags you pass (e.g. `diagnostic:CDP-2431`).
export const captureMessage = (message, { contexts = {}, tags = {}, level = 'info' } = {}) => {
  Sentry.withScope((scope) => {
    scope.setLevel(level)
    Object.entries(tags).forEach(([key, value]) => scope.setTag(key, value))
    Object.entries(contexts).forEach(([key, value]) => scope.setContext(key, value))
    Sentry.captureMessage(message)
  })
}